import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION } from "@/lib/appVersion";
import { captureMetric, setObservabilityContext } from "@/lib/observability";
import { addBreadcrumb, clearBreadcrumbs } from "@/lib/breadcrumbs";

const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Get a date-scoped dedup key so that long-lived native sessions
 * still log page views on subsequent days.
 */
function dedupeKey(path: string): string {
  const day = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `${SESSION_ID}:${day}:${path}`;
}

export function usePageViewTracker() {
  const { pathname } = useLocation();
  const prevPath = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);
  const companyIdRef = useRef<string | null>(null);
  const companyResolvedRef = useRef(false);
  const pendingPath = useRef<string | null>(null);

  const loggedPaths = useRef<Set<string>>(new Set());
  const routeEnteredAt = useRef<number>(typeof performance !== "undefined" ? performance.now() : 0);

  const flushPendingPath = useCallback(() => {
    if (!userIdRef.current) return;
    // Wait for the company lookup. Flushing first wrote rows with a null
    // company_id, which Super Admin then had to guess an owner for.
    if (!companyResolvedRef.current) return;

    // Use pendingPath if set, otherwise fall back to current pathname
    const path = pendingPath.current || window.location.pathname;
    pendingPath.current = null;

    if (!path) return;

    // Deduplicate: don't log the same path+session+day combo repeatedly
    const key = dedupeKey(path);
    if (loggedPaths.current.has(key)) return;
    loggedPaths.current.add(key);

    supabase
      .from("page_view_logs")
      .insert({
        user_id: userIdRef.current,
        company_id: companyIdRef.current,
        session_id: SESSION_ID,
        page_path: path,
        page_title: document.title || null,
        user_agent: navigator.userAgent,
        app_version: APP_VERSION,
      })
      .then(({ error }) => {
        if (error) console.warn("[PageView] log failed:", error.message);
      });
  }, []);

  const resolveCompanyAndFlush = useCallback((userId: string) => {
    const isNewIdentity = userIdRef.current !== userId;
    userIdRef.current = userId;
    if (isNewIdentity) {
      companyResolvedRef.current = false;
      companyIdRef.current = null;
    }
    setObservabilityContext({ userId });

    supabase
      .from("company_members")
      // A user can belong to several companies (their own plus the demo
      // sandbox). Pick deterministically — owned company first, then oldest
      // membership — so every page view is attributed to the same company
      // rather than whichever row the database happened to return.
      .select("company_id, role, is_owner, created_at")
      .eq("user_id", userId)
      .order("is_owner", { ascending: false })
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        companyIdRef.current = data?.company_id ?? null;
        // Resolved either way — a user with no membership must still be logged.
        companyResolvedRef.current = true;
        setObservabilityContext({
          companyId: data?.company_id ?? null,
          userRole: (data as { role?: string } | null)?.role ?? null,
        });
        flushPendingPath();
      });
  }, [flushPendingPath]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        resolveCompanyAndFlush(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        addBreadcrumb("auth", "session_active");
        resolveCompanyAndFlush(session.user.id);
      } else {
        addBreadcrumb("auth", "signed_out");
        clearBreadcrumbs();
        userIdRef.current = null;
        companyIdRef.current = null;
        companyResolvedRef.current = false;
        pendingPath.current = null;
        setObservabilityContext({ userId: null, companyId: null, userRole: null });
      }
    });

    return () => subscription.unsubscribe();
  }, [resolveCompanyAndFlush]);

  useEffect(() => {
    if (pathname === prevPath.current) return;
    prevPath.current = pathname;

    // Telemetry only: how long the previous screen was rendered for.
    try {
      const now = typeof performance !== "undefined" ? performance.now() : 0;
      const elapsed = now - routeEnteredAt.current;
      routeEnteredAt.current = now;
      if (elapsed > 0 && elapsed < 10 * 60 * 1000) {
        captureMetric("screen.view", "screen_time_ms", Math.round(elapsed), { route: pathname });
      }
    } catch {
      /* ignore */
    }
    addBreadcrumb("navigation", "route", { route: pathname.slice(0, 60) });

    pendingPath.current = pathname;
    flushPendingPath();
  }, [pathname, flushPendingPath]);
}
